<template>
    <div>
        <vue-headful :title="'[Agents - '+agentCount+'] - AMOS365'" />

        <h4 class="page-header">
            Agents ({{ agentCount }})

            <router-link to="/agents/create" class="btn btn-sm btn-info" v-if="canManageAgents">
                <i class="fas fa-plus-square"></i> Create
            </router-link>
        </h4>

        <div class="card">
            <div class="card-body">
                <v-server-table ref="agentsTable" url="/agents" :columns="columns" :options="options">

                    <div slot="beforeFilter" style="display: inline-block; margin-right: 5px;">

                    </div>

                    <div slot="afterFilter" class="ml-2">
                        <button class="btn btn-sm btn-default" v-on:click="clearFilters()" title="Clear All Filters">
                            <i class="fas fa-ban"></i>
                        </button>
                        <button class="btn btn-sm btn-default" v-on:click="showFilters = !showFilters" title="Apply Filters">
                            <i class="fas fa-filter"></i>
                            <span class="badge badge-danger" v-if="filterCount > 0">{{ filterCount }}</span>
                        </button>
                        <button type="button" class="btn btn-sm btn-default" v-on:click="exportTable()" :disabled="exportingTable">
                            <template v-if="!exportingTable"><i class="fa fa-file-excel"></i> Export</template>
                            <template v-else><i class="fa fa-spinner fa-spin"></i> Exporting</template>
                        </button>
                    </div>

                    <template slot="id" slot-scope="props">
                        <router-link :to="`/agents/view/${props.row.id}`" class="btn btn-sm btn-default">View</router-link>
                    </template>

                    <template slot="last_name" slot-scope="props">
                        <router-link :to="`/agents/view/${props.row.id}`":class="{'text-muted': (props.row.is_active === 0)}">{{ props.row.last_name }}</router-link>
                    </template>

                    <template slot="first_name" slot-scope="props">
                        <router-link :to="`/agents/view/${props.row.id}`":class="{'text-muted': (props.row.is_active === 0)}">{{ props.row.first_name }}</router-link>
                    </template>

                    <template slot="agency_name" slot-scope="props">
                        <router-link :to="`/agencies/view/${props.row.agency_id}`">{{ props.row.agency_name }}</router-link>
                    </template>

                    <template slot="address" slot-scope="props">
                        {{ props.row.address }}<span v-if="props.row.city">, {{ props.row.city }}</span> <span :title="props.row.state_name" v-if="props.row.state_code">{{ props.row.state_code }}</span>
                    </template>

                    <template slot="date_of_birth" slot-scope="props">
                        <span v-if="props.row.date_of_birth">{{ props.row.date_of_birth | moment("MM/DD/YYYY") }}</span>
                    </template>

                    <template slot="created_at" slot-scope="props">
                        {{ props.row.created_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}
                    </template>

                    <template slot="tax_id" slot-scope="props">
                        {{ props.row.tax_id | taxId }}
                    </template>

                    <template slot="phone" slot-scope="props">
                        {{ props.row.phone | phone }}
                    </template>

                    <template slot="is_active" slot-scope="props">
                        <label class="badge badge-success" v-if="props.row.is_active == 1">Active</label>
                        <label class="badge badge-warning" v-else-if="props.row.is_active == 0">Inactive</label>
                    </template>
                </v-server-table>
            </div>
        </div>

        <b-modal v-model="showFilters" :ref="'agentListFilters'" title="Agent Filters" size="lg" no-fade ok-title="Apply Filters" @ok="applyFilters()">
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">First Name:</div>
                <div class="col-md-7 col-12">
                    <b-form-input v-model="filters['agents.first_name']" placeholder="Enter Agent First Name"></b-form-input>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Last Name:</div>
                <div class="col-md-7 col-12">
                    <b-form-input v-model="filters['agents.last_name']" placeholder="Enter Agent Last Name"></b-form-input>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">NPN:</div>
                <div class="col-md-7 col-12">
                    <b-form-input v-model="filters['agents.npn']" placeholder="Enter Agent NPN"></b-form-input>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Writing#:</div>
                <div class="col-md-7 col-12">
                    <b-form-input v-model="filters['agent_contracts.writing_nbr']" placeholder="Enter Contract Writing#"></b-form-input>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">City:</div>
                <div class="col-md-7 col-12">
                    <b-form-input v-model="filters['record_addresses.city']" placeholder="Enter City"></b-form-input>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">State:</div>
                <div class="col-md-7 col-12">
                    <v-select v-model="filters['record_addresses.state_id']" :options="states" :reduce="state => state.id" label="state_name" placeholder="-- Select a State --" />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Phone#:</div>
                <div class="col-md-7 col-12">
                    <b-form-input v-model="filters['record_phones.phone']" placeholder="Enter Agent Phone"></b-form-input>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Date of Birth:</div>
                <div class="col-md-7 col-12">
                    <date-filter :filter="filters['agents.date_of_birth']" size="sm" />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Active:</div>
                <div class="col-md-7 col-12">
                    <b-form-radio-group v-model="filters['agents.is_active']" :options="activeOptions" plain></b-form-radio-group>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Sells the Product:</div>
                <div class="col-md-7 col-12">
                    <multiselect
                        v-model="selected_products"
                        label="product_name"
                        track-by="id"
                        placeholder="Select Product(s)"
                        open-direction="bottom"
                        :options="products"
                        :multiple="true"
                        :limit-text="limitText"
                        :limit="3"
                        :showLabels="false"
                        @input="handleSelect" />
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import Multiselect from 'vue-multiselect';

    export default {
        components: {
            Multiselect
        },

        data() {
            return {
                agentCount: null,
                exportingTable: false,
                showFilters: false,
                loadFilters: true,
                options: {
                    headings: {
                        id: 'Actions',
                        last_name: 'Last',
                        first_name: 'First',
                        agency_name: 'Agency',
                        phone: 'Phone',
                        email: 'Email',
                        address: 'Address',
                        npn: 'NPN',
                        tax_id: 'Tax ID',
                        res_license_nbr: 'Res License#',
                        date_of_birth: 'DoB',
                        last_4_ssn: 'SSN',
                        is_active: 'Active',
                    },
                    sortable: [
                        'last_name',
                        'first_name',
                        'agency_name',
                    ],
                    orderBy: {
                        ascending: true,
                        column: 'agents.last_name',
                    },
                    texts:{
                        filterPlaceholder:"Search Agents",
                    },
                    responseAdapter: (response) => {
                        //Set the count
                        this.agentCount = response.data.count;

                        //Check for filters
                        if(response.data.filters)
                        {
                            //Set the filters
                            this.setFilters(response.data.filters);
                        }

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Load the Filters
                        if(this.loadFilters)
                        {
                            //Append it
                            data.load_filters = true;

                            //Cancel it
                            this.loadFilters = false;
                        }

                        //Set the product Ids
                        this.filters['agent_contracts.product_id'] = this.product_ids;

                        //Add the type
                        data.filters = this.filters;

                        //Send it
                        return data;
                    },
                    uniqueKey: 'id',
                },

                filters: {
                    'agents.first_name': null,
                    'agents.last_name': null,
                    'agents.npn': null,
                    'agents.is_active': 1,
                    'agent_contracts.writing_nbr': null,
                    'record_addresses.city': null,
                    'record_addresses.state_id': null,
                    'record_phones.phone': null,
                    'agent_contracts.product_id': [],
                    'agents.date_of_birth': {
                        'logic': null,
                        'value': null,
                        'value2': null,
                    },
                },

                states: [],
                products: [],
                selected_products: [],
                product_ids: [],

                activeOptions: [
                  { text: 'All', value: null },
                  { text: 'Active Only', value: 1 },
                  { text: 'Inactive Only', value: 0 },
                ],
            }
        },

        watch: {

        },

        computed: {

            canManageAgents(){
                //Check Permissions
                return this.$root.hasPermission("agents.manage");
            },

            columns(){
                //Permission chekc
                if(this.$root.hasPermission("agents.view.sensitive"))
                {
                    return ['last_name', 'first_name', 'phone', 'email', 'address', 'npn', 'res_license_nbr', 'tax_id', 'last_4_ssn', 'date_of_birth', 'agency_name', 'is_active'];
                }

                //Default
                return ['last_name', 'first_name', 'phone', 'email', 'address', 'npn', 'res_license_nbr', 'agency_name', 'is_active'];
            },

            filterCount(){
                //Get the keys
                var keys = Object.keys(this.filters);
                var count = 0;

                //Loop
                for (var i = keys.length - 1; i >= 0; i--)
                {
                    //Check Special Cases
                    if(keys[i] === 'agents.date_of_birth')
                    {
                        //Check Here to it doesn't flow into the next else if
                        if(this.filters['agents.date_of_birth'].logic !== null)
                        {
                            count++;
                        }
                    }
                    //Check the value
                    else if(Array.isArray(this.filters[keys[i]]) && this.filters[keys[i]].length > 0)
                    {
                        count++;
                    }
                    else if(this.filters[keys[i]] != null && this.filters[keys[i]] != '')
                    {
                        count++;
                    }
                }

                //Send it
                return count;
            },
        },

        methods: {
            setFilters(data){
                //Set all
                this.states = data.states;
                this.products = data.products;
            },

            clearFilters(){
                //Get the keys
                var keys = Object.keys(this.filters);
                var count = 0;

                //Loop
                for (var i = keys.length - 1; i >= 0; i--)
                {
                    //Check the value
                    if(typeof this.filters[keys[i]] === 'object' && !Array.isArray(this.filters[keys[i]]) && this.filters[keys[i]] !== null)
                    {
                        //Check the Logic
                        if(this.filters[keys[i]]['logic'] != null)
                        {
                            count++;
                        }
                    }
                    else if(Array.isArray(this.filters[keys[i]]))
                    {
                        this.filters[keys[i]] = [];
                    }
                    else
                    {
                        this.filters[keys[i]] = null;
                    }
                }

                //Refresh
                this.$refs.agentsTable.refresh();
            },

            applyFilters(){
                //Apply the filters
                this.$refs.agentsTable.refresh();
            },

            limitText (count) {
              return `and ${count} other products`;
            },

            handleSelect(value) {
                //Set the IDs
                this.product_ids = value.map(option => option.id);
                // Optional: Clear the v-model value if you only need the IDs
                // this.selectedOptions = [];
            },

            exportTable(){
                //Loading indicator
                this.exportingTable = true;

                //Build the Filters
                //var filters = filterExtractor.extractFilters(this.filters);

                //Setup Columns
                var cols = JSON.parse(JSON.stringify(this.$refs.agentsTable.columns));

                //Build the payload
                var payload = {
                    filters: this.filters,
                    query: (this.$refs.agentsTable.getRequestParams()).query,
                    orderBy: this.$refs.agentsTable.options.orderBy.column,
                    ascending: this.$refs.agentsTable.options.orderBy.ascending,
                    table: 'agentsTable',
                    columns: cols,
                    export: true,
                };

                //Send it
                this.$http.get('/agents', payload)
                    .then(response => {
                        //Open the new window to Download the File
                        if(response.data.queued)
                        {
                            //Alert the User
                            this.$toastr.s(response.data.message);
                        }
                        else
                        {
                            //Open the download URL
                            window.open(response.data.url);
                        }
                    })
                    .catch(err => {
                        //Alert the user
                        this.$toastr.e(JSON.stringify(err), 'Failed to Export');
                    })
                    .then(response => {
                        //reset
                        this.exportingTable = false;
                    });
            },
        },

        mounted() {
            //Focus on the SEarch Input
            this.$refs.agentsTable.$refs.genericFilter.focus();
        }
    }
</script>
